import { gql, useQuery } from '@apollo/client';
import AddressAutocomplete from '@orn/shared-web/components/AddressAutocomplete';
import Button from '@orn/shared-web/components/Button/Button';
import Input, { usePhoneNumberInput } from '@orn/shared-web/components/Input';
import { MailIcon } from '@orn/shared-web/icons';
import logger from '@orn/shared-web/lib/logger';
import * as React from 'react';

import { ResidenceType } from '../../../__generated__/globalTypes';
import { useDeleteAccount } from '../../../hooks/mutations/useDeleteAccount';
import { useUpdateUser } from '../../../hooks/mutations/useUpdateUser';
import { useUpsertResidence } from '../../../hooks/mutations/useUpsertResidence';
import useAutoCommit from '../../../hooks/useAutoCommit';
import { getOptionalEnv } from '../../../lib/getEnv';
import PhoneVerificationModal from '../../PhoneVerificationModal';
import { CardSection, SettingsCard, SettingsTabLoading, VerifiablePhoneField } from '../common';

import DeleteAccountModal from './DeleteAccountModal';
import { diffPersonalInfo, hasUpdateUserChanges } from './personalInfoDiff';

import type {
  GetPersonalSettingsQuery,
  GetPersonalSettingsQueryVariables,
} from './__generated__/PersonalSettingsForm.gql';
import type { IParsedAddress } from '@orn/shared-web/components/AddressAutocomplete';
import type { IPhoneValidationResult } from '@orn/shared-web/components/Input/PhoneNumberInput';

// ---------------------------------------------------------------------------
// Base (presentational) — renders the UI from props + callbacks.
// ---------------------------------------------------------------------------

interface IPersonalSettingsPhone {
  value: string;
  onChange: (value: string) => void;
  validation: IPhoneValidationResult;
  /** True when the entered number differs from the saved one. */
  changed: boolean;
  /**
   * When they last proved they hold the number, or `null` when nobody has.
   * Read only to decide whether to offer the Verify button — the button's
   * presence is the whole report, and a line beside it saying the same thing
   * only has something to say in the case the button already covers.
   */
  verifiedAt: string | null;
  hasSavedNumber: boolean;
  onVerify: () => void;
}

interface IPersonalSettingsAddress {
  apiKey: string;
  defaultValue: string;
  onSelect: (address: IParsedAddress) => void;
  onInputChange: (input: string) => void;
  /** Validation error shown inline; undefined hides it. */
  error?: string;
}

interface IPersonalSettingsVerification {
  isOpen: boolean;
  /** Saved phone number to verify; the modal renders only when truthy. */
  phoneNumber: string;
  onClose: () => void;
  onVerified: () => void;
}

interface IPersonalSettingsDeletion {
  isModalOpen: boolean;
  /** True while the delete mutation is in flight. */
  isDeleting: boolean;
  /** Error shown in the modal after a failed delete; undefined hides it. */
  error?: string;
  onOpenModal: () => void;
  onCloseModal: () => void;
  onConfirm: () => void;
}

export interface IPersonalSettingsFormBaseProps {
  firstName: string;
  lastName: string;
  preferredName: string;
  email: string;
  onFirstNameChange: (value: string) => void;
  onLastNameChange: (value: string) => void;
  onPreferredNameChange: (value: string) => void;
  /** Validation errors shown inline; undefined hides the hint. */
  firstNameError?: string;
  lastNameError?: string;
  phone: IPersonalSettingsPhone;
  address: IPersonalSettingsAddress;
  verification: IPersonalSettingsVerification;
  deletion: IPersonalSettingsDeletion;
  saveError?: string;
  /** Persist pending edits — wired to the text inputs' blur. */
  onCommit: () => void;
}

/**
 * Presentational "Personal Information" settings form (Figma node `4198:95847`):
 * name, preferred name, read-only email, phone (with inline verification), and
 * primary address, plus the right-aligned Delete account button and the
 * phone-verification and delete-confirmation modals. Fields persist via
 * `onCommit` on blur (address on suggestion pick) — there is no save button.
 * Fully controlled — holds no data or mutations; the `PersonalSettingsForm`
 * wrapper (below) owns state, the query, and the mutation callbacks.
 */
export const PersonalSettingsFormBase = ({
  firstName,
  lastName,
  preferredName,
  email,
  onFirstNameChange,
  onLastNameChange,
  onPreferredNameChange,
  firstNameError,
  lastNameError,
  phone,
  address,
  verification,
  deletion,
  saveError,
  onCommit,
}: IPersonalSettingsFormBaseProps) => {
  return (
    <div className="flex max-w-[736px] flex-col gap-5">
      <SettingsCard title="Personal Information" subtitle="Your name and contact details.">
        <CardSection label="General info">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Input
              label="First name"
              value={firstName}
              onChange={onFirstNameChange}
              onBlur={onCommit}
              size="md"
              isInvalid={Boolean(firstNameError)}
              hint={firstNameError}
            />
            <Input
              label="Last name"
              value={lastName}
              onChange={onLastNameChange}
              onBlur={onCommit}
              size="md"
              isInvalid={Boolean(lastNameError)}
              hint={lastNameError}
            />
          </div>
          <Input
            label="Preferred name"
            value={preferredName}
            onChange={onPreferredNameChange}
            onBlur={onCommit}
            size="md"
            hint="We'll address you by this name in the app and emails"
          />
        </CardSection>

        <div className="border-secondary border-t" />

        <CardSection label="Contact & address">
          <Input
            label="Email address"
            icon={MailIcon}
            value={email}
            size="md"
            isDisabled
            isReadOnly
            tooltip="Your email address can't be changed here."
          />

          <VerifiablePhoneField
            label="Phone number"
            value={phone.value}
            onChange={phone.onChange}
            onBlur={onCommit}
            hint="For account notifications."
            isRequired
            validation={phone.validation}
            verifiedAt={phone.verifiedAt}
            isChanged={phone.changed}
            hasError={!phone.validation.isValid}
            onVerify={phone.onVerify}
            verifyTooltip="You need to verify your phone number before you can send text messages to Mantle."
          />

          <AddressAutocomplete
            apiKey={address.apiKey}
            label="Address"
            hint={address.error}
            defaultValue={address.defaultValue}
            includedPrimaryTypes={['street_address']}
            isRequired
            isInvalid={Boolean(address.error)}
            onSelect={address.onSelect}
            onInputChange={address.onInputChange}
          />
        </CardSection>
      </SettingsCard>

      <div className="flex flex-col gap-2 sm:items-end">
        {saveError && <span className="text-error-primary text-sm">{saveError}</span>}
        <Button
          color="primary-destructive"
          size="md"
          onClick={deletion.onOpenModal}
          isDisabled={deletion.isDeleting}
          className="w-full sm:w-auto"
        >
          Delete account
        </Button>
      </div>

      {verification.phoneNumber && (
        <PhoneVerificationModal
          isOpen={verification.isOpen}
          onClose={verification.onClose}
          onVerified={verification.onVerified}
          phoneNumber={verification.phoneNumber}
        />
      )}

      <DeleteAccountModal
        isOpen={deletion.isModalOpen}
        isDeleting={deletion.isDeleting}
        error={deletion.error}
        onClose={deletion.onCloseModal}
        onConfirm={deletion.onConfirm}
      />
    </div>
  );
};

PersonalSettingsFormBase.displayName = 'PersonalSettingsFormBase';

// ---------------------------------------------------------------------------
// Wrapper (data) — fetches the user + sets up mutations, then renders the base.
// ---------------------------------------------------------------------------

const GET_PERSONAL_SETTINGS = gql`
  query GetPersonalSettings {
    getUser {
      id
      firstName
      lastName
      preferredName
      email
      phoneNumber
      phoneNumberVerifiedAt
      primaryResidence {
        id
        addressLine1
        addressLine2
        city
        state
        postalCode
        country
        formatted
        placeId
        residenceType
        isPrimary
      }
    }
  }
`;

type SettingsUser = NonNullable<GetPersonalSettingsQuery['getUser']>;

const seedAddress = (residence: SettingsUser['primaryResidence']): IParsedAddress | null =>
  residence
    ? {
        formattedAddress: residence.formatted ?? '',
        placeId: residence.placeId ?? '',
        subpremise: residence.addressLine2 ?? undefined,
        city: residence.city,
        state: residence.state ?? undefined,
        postalCode: residence.postalCode ?? undefined,
        country: residence.country,
      }
    : null;

/**
 * "My account" settings form wrapper. Fetches the user's profile + primary
 * residence and owns the editable form state. Edits persist as they happen —
 * each commit point (text-input blur, address suggestion pick) diffs the form
 * against the saved user and fans the changes out to `updateUser`
 * (name/preferred/phone) and `upsertResidence` (address) via
 * `Promise.allSettled`. Renders `PersonalSettingsFormBase` for UI.
 */
const PersonalSettingsForm = () => {
  const apiKey = getOptionalEnv('VITE_GOOGLE_PLACES_API_KEY') ?? '';

  const {
    data,
    loading: queryLoading,
    refetch,
  } = useQuery<GetPersonalSettingsQuery, GetPersonalSettingsQueryVariables>(GET_PERSONAL_SETTINGS);
  const user = data?.getUser;
  const primaryResidence = user?.primaryResidence ?? null;

  const [updateUser] = useUpdateUser();
  const [upsertResidence] = useUpsertResidence();
  const [deleteAccount, { loading: deleteLoading }] = useDeleteAccount();

  const [firstName, setFirstName] = React.useState('');
  const [lastName, setLastName] = React.useState('');
  const [preferredName, setPreferredName] = React.useState('');
  const [saveError, setSaveError] = React.useState<string | null>(null);
  const [showVerificationModal, setShowVerificationModal] = React.useState(false);
  const [showDeleteModal, setShowDeleteModal] = React.useState(false);
  const [deleteError, setDeleteError] = React.useState<string | null>(null);
  const [address, setAddress] = React.useState<IParsedAddress | null>(null);
  const [addressInputText, setAddressInputText] = React.useState('');
  const [createdResidenceId, setCreatedResidenceId] = React.useState<string | null>(null);
  const [hydrated, setHydrated] = React.useState(false);
  // Seeded once at hydration: `usePhoneNumberInput` re-syncs its value whenever
  // its initial-value argument changes, so passing the live cache value would
  // let a save response clobber a phone edit in progress.
  const [seedPhoneNumber, setSeedPhoneNumber] = React.useState('');

  const {
    value: phoneNumber,
    validation: phoneValidation,
    handleChange: handlePhoneNumberChange,
  } = usePhoneNumberInput(seedPhoneNumber, true);

  // Seed editable fields once when data first arrives. Never re-seed on later
  // cache changes — a blur-save's response landing while the user types in the
  // next field would clobber it.
  React.useEffect(() => {
    if (!user || hydrated) {
      return;
    }
    setFirstName(user.firstName ?? '');
    setLastName(user.lastName ?? '');
    setPreferredName(user.preferredName ?? '');
    setSeedPhoneNumber(user.phoneNumber ?? '');
    setAddress(seedAddress(user.primaryResidence));
    setHydrated(true);
  }, [user, hydrated]);

  const handleAddressInputChange = React.useCallback((next: string) => {
    setAddressInputText(next);
    setAddress((prev) => (prev && prev.formattedAddress === next ? prev : null));
  }, []);

  const e164PhoneNumber = phoneValidation.e164Value || phoneNumber;
  const phoneChanged = e164PhoneNumber !== (user?.phoneNumber ?? '');

  // Address only saves on a fresh, structured street pick. A `null` `address`
  // (partial typing / cleared field) is intentionally not a change — the upsert
  // needs a city/country, so this form updates an address but can't blank one out.
  const addressChanged = Boolean(
    address &&
    (address.placeId !== (primaryResidence?.placeId ?? '') ||
      address.formattedAddress !== (primaryResidence?.formatted ?? ''))
  );

  // Each field falls back to its saved value when invalid (blank name, malformed
  // phone), so the diff treats it as unchanged — an invalid field never blocks
  // the others from persisting and never reaches the API.
  const updateDiff = diffPersonalInfo(
    {
      firstName: user?.firstName ?? '',
      lastName: user?.lastName ?? '',
      preferredName: user?.preferredName ?? '',
      phoneNumber: user?.phoneNumber ?? '',
    },
    {
      firstName: firstName.trim() || (user?.firstName ?? ''),
      lastName: lastName.trim() || (user?.lastName ?? ''),
      // The API can't clear preferredName, so an emptied field falls back too.
      preferredName: preferredName.trim() || (user?.preferredName ?? ''),
      phoneNumber: phoneValidation.isValid ? e164PhoneNumber : (user?.phoneNumber ?? ''),
    }
  );

  const existingResidenceId = createdResidenceId ?? primaryResidence?.id ?? null;

  const saveAddress = async () => {
    if (!address || !user) {
      return;
    }
    const addressLine1 =
      address.streetNumber && address.streetName
        ? `${address.streetNumber} ${address.streetName}`
        : (primaryResidence?.addressLine1 ?? null);

    const result = await upsertResidence({
      variables: {
        input: {
          residenceId: existingResidenceId,
          userId: existingResidenceId ? null : user.id,
          isPrimary: true,
          // No residence-type picker here; default to House so the DB CHECK
          // (`residenceTypeOther` required iff type=OTHER) is satisfied.
          residenceType: ResidenceType.House,
          addressLine1,
          addressLine2: address.subpremise ?? null,
          city: address.city ?? '',
          state: address.state ?? null,
          postalCode: address.postalCode ?? null,
          country: address.country ?? '',
          placeId: address.placeId || null,
          formatted: address.formattedAddress || null,
        },
      },
    });
    const upsertedId = result.data?.upsertResidence.id ?? null;
    if (upsertedId && !existingResidenceId) {
      setCreatedResidenceId(upsertedId);
      // Create path only: `User.primaryResidence` stays null after a normalized
      // cache merge, so refetch to flip the reference to the new row.
      await refetch();
    }
  };

  const commit = async () => {
    const tasks: Promise<unknown>[] = [];
    if (hasUpdateUserChanges(updateDiff)) {
      tasks.push(updateUser({ variables: { input: updateDiff } }));
    }
    if (addressChanged) {
      tasks.push(saveAddress());
    }
    if (tasks.length === 0) {
      return;
    }
    setSaveError(null);

    const results = await Promise.allSettled(tasks);
    const failures = results.filter((r): r is PromiseRejectedResult => r.status === 'rejected');
    if (failures.length > 0) {
      failures.forEach((failure) => logger.error('Failed to save personal settings', failure.reason));
      setSaveError("Couldn't save your changes. Please try again.");
    }
  };

  const requestCommit = useAutoCommit(commit);

  // The mutation tombstones the user and ends the session server-side, so a
  // hard redirect lands on the login screen with no stale Apollo cache. On
  // failure the account still exists, so surface an error and keep the modal open.
  const handleDeleteAccount = async () => {
    setDeleteError(null);
    try {
      await deleteAccount();
      window.location.href = '/';
    } catch (error) {
      logger.error('Failed to delete account', error);
      setDeleteError("Couldn't delete your account. Please try again.");
    }
  };

  if (queryLoading || !user || !hydrated) {
    return <SettingsTabLoading />;
  }

  return (
    <PersonalSettingsFormBase
      firstName={firstName}
      lastName={lastName}
      preferredName={preferredName}
      email={user.email ?? ''}
      onFirstNameChange={setFirstName}
      onLastNameChange={setLastName}
      onPreferredNameChange={setPreferredName}
      firstNameError={!firstName.trim() ? 'First name is required' : undefined}
      lastNameError={!lastName.trim() ? 'Last name is required' : undefined}
      phone={{
        value: phoneNumber,
        onChange: handlePhoneNumberChange,
        validation: phoneValidation,
        changed: phoneChanged,
        verifiedAt: user.phoneNumberVerifiedAt ?? null,
        hasSavedNumber: Boolean(user.phoneNumber),
        onVerify: () => setShowVerificationModal(true),
      }}
      address={{
        apiKey,
        defaultValue: primaryResidence?.formatted ?? '',
        onSelect: (parsed) => {
          setAddress(parsed);
          requestCommit();
        },
        onInputChange: handleAddressInputChange,
        error:
          address === null && addressInputText.trim() !== ''
            ? 'Select an address from the suggestions to save it'
            : undefined,
      }}
      verification={{
        isOpen: showVerificationModal,
        phoneNumber: user.phoneNumber ?? '',
        onClose: () => setShowVerificationModal(false),
        onVerified: () => setShowVerificationModal(false),
      }}
      deletion={{
        isModalOpen: showDeleteModal,
        isDeleting: deleteLoading,
        error: deleteError ?? undefined,
        onOpenModal: () => {
          setDeleteError(null);
          setShowDeleteModal(true);
        },
        onCloseModal: () => setShowDeleteModal(false),
        onConfirm: () => void handleDeleteAccount(),
      }}
      saveError={saveError ?? undefined}
      onCommit={requestCommit}
    />
  );
};

PersonalSettingsForm.displayName = 'PersonalSettingsForm';

export default PersonalSettingsForm;
